import type { TaskDecisionPrompt } from '@wix/ai-scheduling-assistant-common-lib/utils';
import { groupWaitingCards, type WaitingCardTask } from './waiting-cards';
import { asksNotOnTasks, type OpenAsksRead, type WebOpenAsk } from './open-asks';

// THE "Action needed" number on web — the ONE function behind the island pill (and its card's header) and the
// Tasks screen's Action needed tab badge, so the two can never state different numbers for the same input. The
// phones have the same function (iOS `ActionNeededCount.total`, Android `actionNeededTotal`), feeding their pill,
// tab, home widgets and Live Activity; the Watch snapshot and the native-data aggregate use common-lib
// `actionNeededCount`. Pure, no runtime imports.
//
// Counted in the things the reader has to deal with:
//  - question CARDS over the waiting tasks — one per open question (one question blocking three tasks is 1), plus one
//    per waiting task that holds no question (`groupWaitingCards`);
//  - the outreach-only questions, minus any a waiting task already asks;
//  - the open asks (`lib/open-asks.ts`), minus any a waiting task already asks.
// A capped list still counts everything: `max(kept, (total ?? list) − dropped)`. An absent read adds nothing.

/** An outreach-only question as the wire carries it (`/api/tasks` `outreachQuestions`). */
export type IslandOutreachQuestion = TaskDecisionPrompt & { outreachId: string; createdDate?: string };

export interface ActionNeededCount {
  questionCards: number;
  outreachQuestions: number;
  openAsks: number;
  total: number;
  /** The outreach questions and asks that survive the dedupe — what a list draws as cards. */
  outreachKept: IslandOutreachQuestion[];
  asksKept: WebOpenAsk[];
}

/**
 * The outreach-only open questions that are NOT already some waiting task's question, and how many there are in
 * all (rules 1 and 3 of the island contract).
 */
export function outreachOnlyQuestions(
  waiting: readonly { decisionPrompts?: { decisionId: string }[] }[],
  outreachQuestions: IslandOutreachQuestion[] = [],
  outreachQuestionsTotal?: number,
): { questions: IslandOutreachQuestion[]; count: number } {
  const onTasks = new Set(waiting.flatMap((task) => (task.decisionPrompts ?? []).map((prompt) => prompt.decisionId)));
  const questions = outreachQuestions.filter((question) => !onTasks.has(question.decisionId));
  const dropped = outreachQuestions.length - questions.length;
  return { questions, count: Math.max(questions.length, (outreachQuestionsTotal ?? outreachQuestions.length) - dropped) };
}

export function actionNeededCount(
  waiting: readonly WaitingCardTask[],
  outreach?: { questions: IslandOutreachQuestion[]; total?: number } | null,
  asks?: OpenAsksRead | null,
): ActionNeededCount {
  const questionCards = groupWaitingCards(waiting).length;
  const outreachPart = outreachOnlyQuestions(waiting as { decisionPrompts?: { decisionId: string }[] }[], outreach?.questions, outreach?.total);
  const askPart = asksNotOnTasks(waiting as { decisionPrompts?: { decisionId: string }[] }[], asks?.asks, asks?.total);
  return {
    questionCards,
    outreachQuestions: outreachPart.count,
    openAsks: askPart.count,
    total: questionCards + outreachPart.count + askPart.count,
    outreachKept: outreachPart.questions,
    asksKept: askPart.asks,
  };
}
